import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Signal,
  viewChild,
  AfterViewInit,
  OnInit,
  ElementRef,
  viewChildren
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { UntilDestroy } from '@ngneat/until-destroy';
import { provideComponentStore } from '@ngrx/component-store';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';

import { TrackByHelper } from 'tiime-expert-utils';
import {
  OverlayBase,
  TiimeOverlayModule,
  TiimeIconModule,
  TiimeTabsModule,
  TiimeLoaderModule,
  TiimeOptionModule,
  TiimeOptionSelectionChange
} from 'tiime-material';

import { ContractApiService } from '@gi/api/services/contract-api.service';
import { ProspectApiService } from '@gi/api/services/prospect-api.service';
import { Contract } from '@gi/models/contract';
import { Prospect } from '@gi/models/prospect';
import { AppStoreState } from '@interfaces/app-store-state';
import { Company } from '@models/company';
import { NavigationService } from '@modules/core/navigation/navigation.service';
import { businessUserCanAccessToContracts } from '@modules/core/store/business-user/business-user-selector';
import { CompanyOptionComponent } from '@modules/shared/options/company-option/company-option.component';
import { ContractOptionComponent } from '@modules/shared/options/contract-option/contract-option.component';
import { ProspectOptionComponent } from '@modules/shared/options/prospect-option/prospect-option.component';
import { CreateOrUpdateContractDialogService } from '@shared-dialogs/create-or-update-contract-dialog/create-or-update-contract-dialog.service';
import { CreateOrUpdateProspectDialogService } from '@shared-dialogs/create-or-update-prospect-dialog/create-or-update-prospect-dialog.service';
import { VirtualScrollViewportSizeDirective } from '@shared-directives/virtual-scroll-viewport-size/virtual-scroll-viewport-size.directive';

import { SEARCH_MIN_LENGTH, SearchOverlayStore } from './search-overlay.store';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-search-overlay',
  standalone: true,
  imports: [
    CommonModule,
    ScrollingModule,
    ReactiveFormsModule,
    TiimeOverlayModule,
    TiimeIconModule,
    TiimeTabsModule,
    TiimeLoaderModule,
    TiimeOptionModule,
    CompanyOptionComponent,
    ContractOptionComponent,
    ProspectOptionComponent,
    VirtualScrollViewportSizeDirective
  ],
  templateUrl: './search-overlay.component.html',
  styleUrls: ['./search-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideComponentStore(SearchOverlayStore)]
})
export class SearchOverlayComponent extends OverlayBase<void> implements OnInit, AfterViewInit {
  readonly searchInput: Signal<ElementRef<HTMLInputElement>> =
    viewChild.required<ElementRef<HTMLInputElement>>('searchInput');

  readonly virtualScrollViewports = viewChildren<CdkVirtualScrollViewport>(CdkVirtualScrollViewport);

  readonly searchControl: FormControl<string> = new FormControl<string>('');
  readonly canAccessToContracts$: Observable<boolean> = this.store.pipe(select(businessUserCanAccessToContracts));
  readonly virtualScrollItemSize = 35;
  readonly virtualScrollBufferPx = 210;
  readonly trackById = TrackByHelper.trackById;
  readonly state = this.searchOverlayStore.state;
  readonly SEARCH_MIN_LENGTH = SEARCH_MIN_LENGTH;

  private readonly subscription: Subscription = new Subscription();

  constructor(
    private readonly store: Store<AppStoreState>,
    private readonly searchOverlayStore: SearchOverlayStore,
    private readonly navigationService: NavigationService,
    private readonly contractApiService: ContractApiService,
    private readonly prospectApiService: ProspectApiService,
    private readonly createOrUpdateContractDialogService: CreateOrUpdateContractDialogService,
    private readonly createOrUpdateProspectDialogService: CreateOrUpdateProspectDialogService
  ) {
    super();
  }

  ngOnInit(): void {
    this.observeSearch();
  }

  ngAfterViewInit(): void {
    this.focusSearchInput();
  }

  focusSearchInput(): void {
    setTimeout(() => this.searchInput().nativeElement.focus());
  }

  selectTab(): void {
    this.virtualScrollViewports().forEach((virtualScrollViewport: CdkVirtualScrollViewport) =>
      setTimeout(() => virtualScrollViewport.checkViewportSize())
    );
  }

  selectCompany(event: TiimeOptionSelectionChange, company: Company): void {
    if (!event.isUserInput) {
      return;
    }

    this.navigationService.navigateToCreationRequestIfNeeded(company, () =>
      this.navigationService.navigateToCompany(company.id)
    );

    this.close();
  }

  selectContract(event: TiimeOptionSelectionChange, contractId: number): void {
    if (!event.isUserInput) {
      return;
    }

    this.contractApiService
      .getContract(contractId)
      .pipe(
        switchMap((contract: Contract) =>
          this.createOrUpdateContractDialogService
            .open({
              data: {
                contract
              }
            })
            .afterClosed()
        ),
        filter((updatedContract: Contract) => !!updatedContract),
        tap((updatedContract: Contract) => this.searchOverlayStore.updateContract(updatedContract))
      )
      .subscribe();
  }

  selectProspect(event: TiimeOptionSelectionChange, prospectId: number): void {
    if (!event.isUserInput) {
      return;
    }

    this.prospectApiService
      .getProspect(prospectId)
      .pipe(
        switchMap((prospect: Prospect) =>
          this.createOrUpdateProspectDialogService
            .open({
              data: {
                prospect,
                displayNavigateToCompany: true
              }
            })
            .afterClosed()
        ),
        filter((updatedProspect: Prospect) => !!updatedProspect),
        tap((updatedProspect: Prospect) => this.searchOverlayStore.updateProspect(updatedProspect))
      )
      .subscribe();
  }

  private observeSearch(): void {
    this.subscription.add(
      this.searchControl.valueChanges.pipe(tap((search: string) => this.search(search))).subscribe()
    );
  }

  private search(search: string): void {
    this.searchOverlayStore.searchCompanies(search);

    this.subscription.add(
      this.canAccessToContracts$
        .pipe(
          take(1),
          filter((canAccessToContracts: boolean) => canAccessToContracts),
          tap(() => {
            this.searchOverlayStore.searchProspects(search);
            this.searchOverlayStore.searchContracts(search);
          })
        )
        .subscribe()
    );
  }
}
